import ArtistFormBaseController from "./artist_form_base"

export default class extends ArtistFormBaseController {
  static targets = [
    // Base targets
    "form", "nameInput", "appleIdInput", "newAppleCheckbox", "spotifyUriInput", "newSpotifyCheckbox",
    "errors", "errorList", "submitButton", "submitText", "submitSpinner", "contributorFlag",
    // Apple search targets
    "appleOptionSearch", "appleOptionManual", "appleOptionNew",
    "appleSearchContainer", "appleManualContainer", "appleNewContainer",
    "appleSearchInputWrapper", "appleSearchInput", "appleSearchResults",
    "appleSelectedArtist", "appleSelectedImage", "appleSelectedFallbackIcon", "appleSelectedName", "appleSelectedInfo",
    "appleIdHidden",
    // Spotify search targets
    "spotifyOptionSearch", "spotifyOptionManual", "spotifyOptionNew",
    "spotifySearchContainer", "spotifyManualContainer", "spotifyNewContainer",
    "spotifySearchInputWrapper", "spotifySearchInput", "spotifySearchResults",
    "spotifySelectedArtist", "spotifySelectedImage", "spotifySelectedFallbackIcon", "spotifySelectedName", "spotifySelectedInfo",
    "spotifyUriHidden",
    // Edit-specific targets
    "artistIdInput"
  ]

  connect() {
    super.connect()
    this.spotifySearchTimeout = null
    this.appleSearchTimeout = null
  }

  // Override resetForm to also reset Apple and Spotify state
  resetForm() {
    super.resetForm()
    this.resetAppleState()
    this.resetSpotifyState()
  }

  resetAppleState() {
    // Reset to "Search Artist by Name" option (default)
    if (this.hasAppleOptionSearchTarget) {
      this.appleOptionSearchTarget.checked = true
    }
    if (this.hasAppleOptionManualTarget) {
      this.appleOptionManualTarget.checked = false
    }
    if (this.hasAppleOptionNewTarget) {
      this.appleOptionNewTarget.checked = false
    }

    // Show search container, hide manual and new containers
    if (this.hasAppleSearchContainerTarget) {
      this.appleSearchContainerTarget.classList.remove('hidden')
    }
    if (this.hasAppleManualContainerTarget) {
      this.appleManualContainerTarget.classList.add('hidden')
    }
    if (this.hasAppleNewContainerTarget) {
      this.appleNewContainerTarget.classList.add('hidden')
    }

    // Clear hidden fields
    if (this.hasAppleIdHiddenTarget) {
      this.appleIdHiddenTarget.value = ''
    }
    if (this.hasNewAppleCheckboxTarget) {
      this.newAppleCheckboxTarget.value = '0'
    }

    // Clear search state
    this.clearAppleSearchState()
  }

  resetSpotifyState() {
    // Reset to "Search Artist by Name" option (default)
    if (this.hasSpotifyOptionSearchTarget) {
      this.spotifyOptionSearchTarget.checked = true
    }
    if (this.hasSpotifyOptionManualTarget) {
      this.spotifyOptionManualTarget.checked = false
    }
    if (this.hasSpotifyOptionNewTarget) {
      this.spotifyOptionNewTarget.checked = false
    }

    // Show search container, hide manual and new containers
    if (this.hasSpotifySearchContainerTarget) {
      this.spotifySearchContainerTarget.classList.remove('hidden')
    }
    if (this.hasSpotifyManualContainerTarget) {
      this.spotifyManualContainerTarget.classList.add('hidden')
    }
    if (this.hasSpotifyNewContainerTarget) {
      this.spotifyNewContainerTarget.classList.add('hidden')
    }

    // Clear hidden fields
    if (this.hasSpotifyUriHiddenTarget) {
      this.spotifyUriHiddenTarget.value = ''
    }
    if (this.hasNewSpotifyCheckboxTarget) {
      this.newSpotifyCheckboxTarget.value = '0'
    }
    // Clear search state
    this.clearSpotifySearchState()
  }

  // Implementation of abstract methods from base class
  async submitForm(formData) {
    const artistId = this.artistIdInputTarget.value
    return await this.fetchData(`/artists/${artistId}`, {
      method: 'PATCH',
      body: formData
    })
  }

  handleSubmitSuccess(artist) {
    this.updateArtistInDropdown(artist)
  }

  // Override validateForm to add uniqueness checks
  validateForm() {
    const errors = super.validateForm()

    // Check for potential uniqueness issues
    if (this.hasAppleIdInputTarget && this.appleIdInputTarget.classList.contains('border-yellow-500')) {
      errors.push("Apple ID appears to already be in use. Please choose a different one or select 'New Apple Artist'")
    }

    return errors
  }

  updateArtistInDropdown(artist) {
    // Update artist tags in primary artist search controller (Album)
    const artistSearchElement = document.querySelector('[data-controller~="artist-search"]')
    if (artistSearchElement) {
      const artistSearchController = this.application.getControllerForElementAndIdentifier(artistSearchElement, 'artist-search')
      if (artistSearchController) {
        artistSearchController.updateArtistTag(artist)
      }
    }

    // Update artist tags in primary artist search controller (Track)
    const trackArtistSearchElement = document.querySelector('[data-controller~="track-artist-search"]')
    if (trackArtistSearchElement) {
      const trackArtistSearchController = this.application.getControllerForElementAndIdentifier(trackArtistSearchElement, 'track-artist-search')
      if (trackArtistSearchController) {
        trackArtistSearchController.updateArtistTag(artist)
      }
    }

    // Update artist tags in featuring artist search controller (Album)
    const featuringArtistSearchElement = document.querySelector('[data-controller~="featuring-artist-search"]')
    if (featuringArtistSearchElement) {
      const featuringArtistSearchController = this.application.getControllerForElementAndIdentifier(featuringArtistSearchElement, 'featuring-artist-search')
      if (featuringArtistSearchController) {
        featuringArtistSearchController.updateArtistTag(artist)
      }
    }

    // Update artist tags in featuring artist search controller (Track)
    const trackFeaturingArtistSearchElement = document.querySelector('[data-controller~="track-featuring-artist-search"]')
    if (trackFeaturingArtistSearchElement) {
      const trackFeaturingArtistSearchController = this.application.getControllerForElementAndIdentifier(trackFeaturingArtistSearchElement, 'track-featuring-artist-search')
      if (trackFeaturingArtistSearchController) {
        trackFeaturingArtistSearchController.updateArtistTag(artist)
      }
    }

    // Update artist tags in all role artist search controllers (Album)
    const roleArtistSearchElements = document.querySelectorAll('[data-controller~="role-artist-search"]')
    roleArtistSearchElements.forEach(element => {
      const roleArtistSearchController = this.application.getControllerForElementAndIdentifier(element, 'role-artist-search')
      if (roleArtistSearchController) {
        roleArtistSearchController.updateArtistTag(artist)
      }
    })

    // Update artist tags in all role artist search controllers (Track)
    const trackRoleArtistSearchElements = document.querySelectorAll('[data-controller~="track-role-artist-search"]')
    trackRoleArtistSearchElements.forEach(element => {
      const trackRoleArtistSearchController = this.application.getControllerForElementAndIdentifier(element, 'track-role-artist-search')
      if (trackRoleArtistSearchController) {
        trackRoleArtistSearchController.updateArtistTag(artist)
      }
    })
  }

  // Method to populate form fields with existing artist data
  populateForm(artist) {
    // Always reset state first to clear any cached data from previous artist
    this.resetAppleState()
    this.resetSpotifyState()

    if (artist) {
      this.nameInputTarget.value = artist.name || ''
      this.artistIdInputTarget.value = artist.id || ''

      // Handle Apple Music data
      if (artist.apple_id) {
        // Show search option with selected artist widget (like after selecting from search)
        if (this.hasAppleOptionSearchTarget) {
          this.appleOptionSearchTarget.checked = true
        }
        if (this.hasAppleOptionManualTarget) {
          this.appleOptionManualTarget.checked = false
        }
        if (this.hasAppleOptionNewTarget) {
          this.appleOptionNewTarget.checked = false
        }
        if (this.hasAppleSearchContainerTarget) {
          this.appleSearchContainerTarget.classList.remove('hidden')
        }
        if (this.hasAppleManualContainerTarget) {
          this.appleManualContainerTarget.classList.add('hidden')
        }
        if (this.hasAppleNewContainerTarget) {
          this.appleNewContainerTarget.classList.add('hidden')
        }
        // Hide search input wrapper, show selected artist widget
        if (this.hasAppleSearchInputWrapperTarget) {
          this.appleSearchInputWrapperTarget.classList.add('hidden')
        }
        if (this.hasAppleSelectedArtistTarget) {
          this.appleSelectedArtistTarget.classList.remove('hidden')
          // Handle image from database
          if (this.hasAppleSelectedImageTarget && this.hasAppleSelectedFallbackIconTarget) {
            if (artist.apple_thumbnail_url) {
              this.appleSelectedImageTarget.src = artist.apple_thumbnail_url
              this.appleSelectedImageTarget.classList.remove('hidden')
              this.appleSelectedFallbackIconTarget.classList.add('hidden')
            } else {
              this.appleSelectedImageTarget.classList.add('hidden')
              this.appleSelectedFallbackIconTarget.classList.remove('hidden')
            }
          }
          if (this.hasAppleSelectedNameTarget) {
            this.appleSelectedNameTarget.textContent = artist.apple_name || artist.name || ''
          }
          if (this.hasAppleSelectedInfoTarget) {
            const genres = artist.apple_genres || []
            const genresDisplay = genres.slice(0, 2).join(', ')
            let infoText = `Apple ID: ${artist.apple_id}`
            if (genresDisplay) {
              infoText += ` - ${genresDisplay}`
            }
            this.appleSelectedInfoTarget.textContent = infoText
          }
        }
        if (this.hasAppleIdHiddenTarget) {
          this.appleIdHiddenTarget.value = artist.apple_id
        }
        if (this.hasNewAppleCheckboxTarget) {
          this.newAppleCheckboxTarget.value = '0'
        }
      } else if (artist.new_apple_artist) {
        // Select new artist option
        if (this.hasAppleOptionNewTarget) {
          this.appleOptionNewTarget.checked = true
          this.appleOptionSearchTarget.checked = false
          this.appleOptionManualTarget.checked = false
        }
        if (this.hasAppleSearchContainerTarget) {
          this.appleSearchContainerTarget.classList.add('hidden')
        }
        if (this.hasAppleManualContainerTarget) {
          this.appleManualContainerTarget.classList.add('hidden')
        }
        if (this.hasAppleNewContainerTarget) {
          this.appleNewContainerTarget.classList.remove('hidden')
        }
        if (this.hasNewAppleCheckboxTarget) {
          this.newAppleCheckboxTarget.value = '1'
        }
      } else {
        // No Apple ID - show search option by default
        if (this.hasAppleOptionSearchTarget) {
          this.appleOptionSearchTarget.checked = true
        }
        if (this.hasAppleSearchContainerTarget) {
          this.appleSearchContainerTarget.classList.remove('hidden')
        }
        if (this.hasAppleManualContainerTarget) {
          this.appleManualContainerTarget.classList.add('hidden')
        }
        if (this.hasAppleNewContainerTarget) {
          this.appleNewContainerTarget.classList.add('hidden')
        }
      }

      // Handle Spotify data
      if (artist.spotify_uri) {
        // Show search option with selected artist widget (like after selecting from search)
        if (this.hasSpotifyOptionSearchTarget) {
          this.spotifyOptionSearchTarget.checked = true
        }
        if (this.hasSpotifyOptionManualTarget) {
          this.spotifyOptionManualTarget.checked = false
        }
        if (this.hasSpotifyOptionNewTarget) {
          this.spotifyOptionNewTarget.checked = false
        }
        if (this.hasSpotifySearchContainerTarget) {
          this.spotifySearchContainerTarget.classList.remove('hidden')
        }
        if (this.hasSpotifyManualContainerTarget) {
          this.spotifyManualContainerTarget.classList.add('hidden')
        }
        if (this.hasSpotifyNewContainerTarget) {
          this.spotifyNewContainerTarget.classList.add('hidden')
        }
        // Hide search input wrapper, show selected artist widget
        if (this.hasSpotifySearchInputWrapperTarget) {
          this.spotifySearchInputWrapperTarget.classList.add('hidden')
        }
        if (this.hasSpotifySelectedArtistTarget) {
          this.spotifySelectedArtistTarget.classList.remove('hidden')
          // Use stored Spotify data from database
          if (this.hasSpotifySelectedNameTarget) {
            this.spotifySelectedNameTarget.textContent = artist.spotify_name || artist.name || ''
          }
          if (this.hasSpotifySelectedInfoTarget) {
            const followers = artist.spotify_followers_count ? this.formatNumber(artist.spotify_followers_count) : '0'
            this.spotifySelectedInfoTarget.textContent =
              this.spotifyInfoText(artist.spotify_uri, followers, artist.spotify_genres)
          }
          // Handle image from database
          if (this.hasSpotifySelectedImageTarget && this.hasSpotifySelectedFallbackIconTarget) {
            if (artist.spotify_thumbnail_uri) {
              this.spotifySelectedImageTarget.src = artist.spotify_thumbnail_uri
              this.spotifySelectedImageTarget.classList.remove('hidden')
              this.spotifySelectedFallbackIconTarget.classList.add('hidden')
            } else {
              this.spotifySelectedImageTarget.classList.add('hidden')
              this.spotifySelectedFallbackIconTarget.classList.remove('hidden')
            }
          }
        }
        if (this.hasSpotifyUriHiddenTarget) {
          this.spotifyUriHiddenTarget.value = artist.spotify_uri
        }
        if (this.hasNewSpotifyCheckboxTarget) {
          this.newSpotifyCheckboxTarget.value = '0'
        }
      } else if (artist.new_spotify_artist) {
        // Select new artist option
        if (this.hasSpotifyOptionNewTarget) {
          this.spotifyOptionNewTarget.checked = true
          this.spotifyOptionSearchTarget.checked = false
          this.spotifyOptionManualTarget.checked = false
        }
        if (this.hasSpotifySearchContainerTarget) {
          this.spotifySearchContainerTarget.classList.add('hidden')
        }
        if (this.hasSpotifyManualContainerTarget) {
          this.spotifyManualContainerTarget.classList.add('hidden')
        }
        if (this.hasSpotifyNewContainerTarget) {
          this.spotifyNewContainerTarget.classList.remove('hidden')
        }
        if (this.hasNewSpotifyCheckboxTarget) {
          this.newSpotifyCheckboxTarget.value = '1'
        }
      } else {
        // No Spotify URI - show search option by default
        if (this.hasSpotifyOptionSearchTarget) {
          this.spotifyOptionSearchTarget.checked = true
        }
        if (this.hasSpotifySearchContainerTarget) {
          this.spotifySearchContainerTarget.classList.remove('hidden')
        }
        if (this.hasSpotifyManualContainerTarget) {
          this.spotifyManualContainerTarget.classList.add('hidden')
        }
        if (this.hasSpotifyNewContainerTarget) {
          this.spotifyNewContainerTarget.classList.add('hidden')
        }
      }
    }
  }

  // Apple ID validation
  validateAppleId() {
    if (!this.hasAppleIdInputTarget) return

    const appleId = this.appleIdInputTarget.value.trim()

    // Reset styles
    this.appleIdInputTarget.classList.remove('border-yellow-500', 'border-red-500')
    this.appleIdInputTarget.classList.add('border-gray-300')

    if (appleId) {
      // Check format
      if (!/^\d+$/.test(appleId)) {
        this.appleIdInputTarget.classList.remove('border-gray-300')
        this.appleIdInputTarget.classList.add('border-red-500')
        return
      }

      // Update hidden field
      if (this.hasAppleIdHiddenTarget) {
        this.appleIdHiddenTarget.value = appleId
      }

      // Check for potential duplicates
      this.checkAppleIdUniqueness(appleId)
    }
  }

  // Spotify URI validation
  validateSpotifyUri() {
    if (!this.hasSpotifyUriInputTarget) return

    const spotifyUri = this.spotifyUriInputTarget.value.trim()

    // Reset styles
    this.spotifyUriInputTarget.classList.remove('border-yellow-500', 'border-red-500')
    this.spotifyUriInputTarget.classList.add('border-gray-300')

    if (spotifyUri) {
      // Allow just the ID or full URI format
      const isValidFormat = /^spotify:artist:[a-zA-Z0-9]+$/.test(spotifyUri) ||
                           /^[a-zA-Z0-9]+$/.test(spotifyUri)
      if (!isValidFormat) {
        this.spotifyUriInputTarget.classList.remove('border-gray-300')
        this.spotifyUriInputTarget.classList.add('border-red-500')
      } else {
        // Update hidden field with proper format
        this.updateSpotifyHiddenField(spotifyUri)
      }
    }
  }

  async checkAppleIdUniqueness(appleId) {
    // Simplified uniqueness check - in production you might want to debounce this
    try {
      const currentArtistId = this.artistIdInputTarget.value
      const { data, success } = await this.fetchData(`/artists/check_uniqueness?apple_id=${appleId}&exclude_id=${currentArtistId}`)

      if (success && data.exists) {
        this.appleIdInputTarget.classList.remove('border-gray-300')
        this.appleIdInputTarget.classList.add('border-yellow-500')
      }
    } catch (error) {
      console.log('Could not check Apple ID uniqueness:', error)
    }
  }

  // Toggle between Apple options (search, manual, new)
  toggleAppleOption(event) {
    const value = event.target.value

    // Hide all containers
    if (this.hasAppleSearchContainerTarget) {
      this.appleSearchContainerTarget.classList.add('hidden')
    }
    if (this.hasAppleManualContainerTarget) {
      this.appleManualContainerTarget.classList.add('hidden')
    }
    if (this.hasAppleNewContainerTarget) {
      this.appleNewContainerTarget.classList.add('hidden')
    }

    // Clear previous values
    if (this.hasAppleIdHiddenTarget) {
      this.appleIdHiddenTarget.value = ''
    }
    if (this.hasNewAppleCheckboxTarget) {
      this.newAppleCheckboxTarget.value = '0'
    }

    // Show appropriate container and set values
    switch (value) {
      case 'search':
        if (this.hasAppleSearchContainerTarget) {
          this.appleSearchContainerTarget.classList.remove('hidden')
          if (this.hasAppleSearchInputTarget) {
            this.appleSearchInputTarget.focus()
          }
        }
        break
      case 'manual':
        if (this.hasAppleManualContainerTarget) {
          this.appleManualContainerTarget.classList.remove('hidden')
          if (this.hasAppleIdInputTarget) {
            this.appleIdInputTarget.focus()
          }
        }
        break
      case 'new':
        if (this.hasAppleNewContainerTarget) {
          this.appleNewContainerTarget.classList.remove('hidden')
        }
        if (this.hasNewAppleCheckboxTarget) {
          this.newAppleCheckboxTarget.value = '1'
        }
        break
    }

    // Clear search results and selection
    this.clearAppleSearchState()
  }

  // Search Apple Music artists
  searchApple() {
    const query = this.appleSearchInputTarget.value.trim()

    // Clear previous timeout
    if (this.appleSearchTimeout) {
      clearTimeout(this.appleSearchTimeout)
    }

    if (query.length < 2) {
      this.appleSearchResultsTarget.classList.add('hidden')
      return
    }

    // Debounce the search
    this.appleSearchTimeout = setTimeout(() => {
      this.performAppleSearch(query)
    }, 300)
  }

  async performAppleSearch(query) {
    this.appleSearchResultsTarget.classList.remove('hidden')
    this.appleSearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Searching Apple Music...</div>'

    try {
      const response = await fetch(`/artists/apple_search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      const artists = await response.json()
      this.displayAppleResults(artists)
    } catch (error) {
      console.error('Apple search error:', error)
      this.appleSearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-red-500">Error searching Apple Music</div>'
    }
  }

  displayAppleResults(artists) {
    if (!artists || artists.length === 0) {
      this.appleSearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No artists found</div>'
      return
    }

    let html = '<div class="divide-y divide-gray-200">'

    artists.forEach(artist => {
      const artworkUrl = artist.artwork_url || ''
      const genres = artist.genres || []
      const genresDisplay = genres.slice(0, 2).join(', ')

      html += `
        <div class="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
             data-action="click->${this.identifier}#selectAppleArtist"
             data-apple-id="${artist.apple_id}"
             data-name="${this.escapeHtml(artist.name)}"
             data-artwork-url="${artworkUrl}"
             data-genres='${JSON.stringify(genres)}'>
          <div class="flex-shrink-0">
            ${artworkUrl
              ? `<img src="${artworkUrl}" alt="${this.escapeHtml(artist.name)}" class="w-10 h-10 rounded-full object-cover">`
              : `<div class="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
                   <svg class="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                     <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                   </svg>
                 </div>`
            }
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate">${this.escapeHtml(artist.name)}</div>
            <div class="text-xs text-gray-500">Apple ID: ${artist.apple_id}${genresDisplay ? ` - ${this.escapeHtml(genresDisplay)}` : ''}</div>
          </div>
        </div>
      `
    })

    html += '</div>'
    this.appleSearchResultsTarget.innerHTML = html
  }

  selectAppleArtist(event) {
    const element = event.currentTarget
    const appleId = element.dataset.appleId
    const name = element.dataset.name
    const artworkUrl = element.dataset.artworkUrl
    let genres = []
    try {
      genres = JSON.parse(element.dataset.genres || '[]')
    } catch (e) {
      genres = []
    }

    // Update hidden field
    if (this.hasAppleIdHiddenTarget) {
      this.appleIdHiddenTarget.value = appleId
    }

    // Update new_apple_artist to false since we selected an existing one
    if (this.hasNewAppleCheckboxTarget) {
      this.newAppleCheckboxTarget.value = '0'
    }

    // Hide search results
    this.appleSearchResultsTarget.classList.add('hidden')

    // Show selected artist
    if (this.hasAppleSelectedArtistTarget) {
      this.appleSelectedArtistTarget.classList.remove('hidden')
      if (this.hasAppleSelectedImageTarget && this.hasAppleSelectedFallbackIconTarget) {
        if (artworkUrl) {
          this.appleSelectedImageTarget.src = artworkUrl
          this.appleSelectedImageTarget.classList.remove('hidden')
          this.appleSelectedFallbackIconTarget.classList.add('hidden')
        } else {
          this.appleSelectedImageTarget.classList.add('hidden')
          this.appleSelectedFallbackIconTarget.classList.remove('hidden')
        }
      }
      if (this.hasAppleSelectedNameTarget) {
        this.appleSelectedNameTarget.textContent = name
      }
      if (this.hasAppleSelectedInfoTarget) {
        const genresDisplay = genres.slice(0, 2).join(', ')
        let infoText = `Apple ID: ${appleId}`
        if (genresDisplay) {
          infoText += ` - ${genresDisplay}`
        }
        this.appleSelectedInfoTarget.textContent = infoText
      }
    }

    // Hide search input wrapper
    if (this.hasAppleSearchInputWrapperTarget) {
      this.appleSearchInputWrapperTarget.classList.add('hidden')
    }
  }

  clearAppleSelection() {
    // Clear hidden field
    if (this.hasAppleIdHiddenTarget) {
      this.appleIdHiddenTarget.value = ''
    }

    // Hide selected artist display
    if (this.hasAppleSelectedArtistTarget) {
      this.appleSelectedArtistTarget.classList.add('hidden')
    }

    // Show search input wrapper and clear input
    if (this.hasAppleSearchInputWrapperTarget) {
      this.appleSearchInputWrapperTarget.classList.remove('hidden')
    }
    if (this.hasAppleSearchInputTarget) {
      this.appleSearchInputTarget.value = ''
      this.appleSearchInputTarget.focus()
    }

    // Hide results
    if (this.hasAppleSearchResultsTarget) {
      this.appleSearchResultsTarget.classList.add('hidden')
    }
  }

  clearAppleSearchState() {
    if (this.hasAppleSearchInputWrapperTarget) {
      this.appleSearchInputWrapperTarget.classList.remove('hidden')
    }
    if (this.hasAppleSearchInputTarget) {
      this.appleSearchInputTarget.value = ''
    }
    if (this.hasAppleSearchResultsTarget) {
      this.appleSearchResultsTarget.classList.add('hidden')
      this.appleSearchResultsTarget.innerHTML = ''
    }
    if (this.hasAppleSelectedArtistTarget) {
      this.appleSelectedArtistTarget.classList.add('hidden')
    }
    // Reset image/fallback visibility
    if (this.hasAppleSelectedImageTarget) {
      this.appleSelectedImageTarget.classList.add('hidden')
    }
    if (this.hasAppleSelectedFallbackIconTarget) {
      this.appleSelectedFallbackIconTarget.classList.remove('hidden')
    }
    if (this.hasAppleIdInputTarget) {
      this.appleIdInputTarget.value = ''
    }
  }

  // Toggle between Spotify options (search, manual, new)
  toggleSpotifyOption(event) {
    const value = event.target.value

    // Hide all containers
    if (this.hasSpotifySearchContainerTarget) {
      this.spotifySearchContainerTarget.classList.add('hidden')
    }
    if (this.hasSpotifyManualContainerTarget) {
      this.spotifyManualContainerTarget.classList.add('hidden')
    }
    if (this.hasSpotifyNewContainerTarget) {
      this.spotifyNewContainerTarget.classList.add('hidden')
    }

    // Clear previous values
    if (this.hasSpotifyUriHiddenTarget) {
      this.spotifyUriHiddenTarget.value = ''
    }
    if (this.hasNewSpotifyCheckboxTarget) {
      this.newSpotifyCheckboxTarget.value = '0'
    }

    // Show appropriate container and set values
    switch (value) {
      case 'search':
        if (this.hasSpotifySearchContainerTarget) {
          this.spotifySearchContainerTarget.classList.remove('hidden')
          if (this.hasSpotifySearchInputTarget) {
            this.spotifySearchInputTarget.focus()
          }
        }
        break
      case 'manual':
        if (this.hasSpotifyManualContainerTarget) {
          this.spotifyManualContainerTarget.classList.remove('hidden')
          if (this.hasSpotifyUriInputTarget) {
            this.spotifyUriInputTarget.focus()
          }
        }
        break
      case 'new':
        if (this.hasSpotifyNewContainerTarget) {
          this.spotifyNewContainerTarget.classList.remove('hidden')
        }
        if (this.hasNewSpotifyCheckboxTarget) {
          this.newSpotifyCheckboxTarget.value = '1'
        }
        break
    }

    // Clear search results and selection
    this.clearSpotifySearchState()
  }

  // Search Spotify artists
  searchSpotify() {
    const query = this.spotifySearchInputTarget.value.trim()

    // Clear previous timeout
    if (this.spotifySearchTimeout) {
      clearTimeout(this.spotifySearchTimeout)
    }

    if (query.length < 2) {
      this.spotifySearchResultsTarget.classList.add('hidden')
      return
    }

    // Debounce the search
    this.spotifySearchTimeout = setTimeout(() => {
      this.performSpotifySearch(query)
    }, 300)
  }

  async performSpotifySearch(query) {
    this.spotifySearchResultsTarget.classList.remove('hidden')
    this.spotifySearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">Searching Spotify...</div>'

    try {
      const response = await fetch(`/artists/spotify_search?query=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      const artists = await response.json()
      this.displaySpotifyResults(artists)
    } catch (error) {
      console.error('Spotify search error:', error)
      this.spotifySearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-red-500">Error searching Spotify</div>'
    }
  }

  displaySpotifyResults(artists) {
    if (!artists || artists.length === 0) {
      this.spotifySearchResultsTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No artists found</div>'
      return
    }

    let html = '<div class="divide-y divide-gray-200">'

    artists.forEach(artist => {
      const imageUrl = artist.image_url || ''
      const followersCount = artist.followers || 0
      const followersDisplay = followersCount ? this.formatNumber(followersCount) : '0'
      const genres = artist.genres || []
      const genresDisplay = genres.slice(0, 2).join(', ')

      html += `
        <div class="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
             data-action="click->${this.identifier}#selectSpotifyArtist"
             data-spotify-uri="${artist.spotify_uri}"
             data-name="${this.escapeHtml(artist.name)}"
             data-image-url="${imageUrl}"
             data-followers-count="${followersCount}"
             data-followers-display="${followersDisplay}"
             data-genres='${JSON.stringify(genres)}'>
          <div class="flex-shrink-0">
            ${imageUrl
              ? `<img src="${imageUrl}" alt="${this.escapeHtml(artist.name)}" class="w-10 h-10 rounded-full object-cover">`
              : `<div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                   <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                   </svg>
                 </div>`
            }
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate">${this.escapeHtml(artist.name)}</div>
            <div class="text-xs text-gray-500">${followersDisplay} followers${genresDisplay ? ` - ${this.escapeHtml(genresDisplay)}` : ''}</div>
          </div>
        </div>
      `
    })

    html += '</div>'
    this.spotifySearchResultsTarget.innerHTML = html
  }

  selectSpotifyArtist(event) {
    const element = event.currentTarget
    const spotifyUri = element.dataset.spotifyUri
    const name = element.dataset.name
    const imageUrl = element.dataset.imageUrl
    const followersDisplay = element.dataset.followersDisplay
    let genres = []
    try {
      genres = JSON.parse(element.dataset.genres || '[]')
    } catch (e) {
      genres = []
    }

    // Update hidden field
    if (this.hasSpotifyUriHiddenTarget) {
      this.spotifyUriHiddenTarget.value = spotifyUri
    }

    // Update new_spotify_artist to false since we selected an existing one
    if (this.hasNewSpotifyCheckboxTarget) {
      this.newSpotifyCheckboxTarget.value = '0'
    }

    // Hide search results
    this.spotifySearchResultsTarget.classList.add('hidden')

    // Show selected artist
    if (this.hasSpotifySelectedArtistTarget) {
      this.spotifySelectedArtistTarget.classList.remove('hidden')
      if (this.hasSpotifySelectedImageTarget && this.hasSpotifySelectedFallbackIconTarget) {
        if (imageUrl) {
          this.spotifySelectedImageTarget.src = imageUrl
          this.spotifySelectedImageTarget.classList.remove('hidden')
          this.spotifySelectedFallbackIconTarget.classList.add('hidden')
        } else {
          this.spotifySelectedImageTarget.classList.add('hidden')
          this.spotifySelectedFallbackIconTarget.classList.remove('hidden')
        }
      }
      if (this.hasSpotifySelectedNameTarget) {
        this.spotifySelectedNameTarget.textContent = name
      }
      if (this.hasSpotifySelectedInfoTarget) {
        this.spotifySelectedInfoTarget.textContent =
          this.spotifyInfoText(spotifyUri, followersDisplay, genres)
      }
    }

    // Hide search input wrapper
    if (this.hasSpotifySearchInputWrapperTarget) {
      this.spotifySearchInputWrapperTarget.classList.add('hidden')
    }
  }

  clearSpotifySelection() {
    // Clear hidden field
    if (this.hasSpotifyUriHiddenTarget) {
      this.spotifyUriHiddenTarget.value = ''
    }

    // Hide selected artist display
    if (this.hasSpotifySelectedArtistTarget) {
      this.spotifySelectedArtistTarget.classList.add('hidden')
    }

    // Show search input wrapper and clear input
    if (this.hasSpotifySearchInputWrapperTarget) {
      this.spotifySearchInputWrapperTarget.classList.remove('hidden')
    }
    if (this.hasSpotifySearchInputTarget) {
      this.spotifySearchInputTarget.value = ''
      this.spotifySearchInputTarget.focus()
    }

    // Hide results
    if (this.hasSpotifySearchResultsTarget) {
      this.spotifySearchResultsTarget.classList.add('hidden')
    }
  }

  clearSpotifySearchState() {
    if (this.hasSpotifySearchInputWrapperTarget) {
      this.spotifySearchInputWrapperTarget.classList.remove('hidden')
    }
    if (this.hasSpotifySearchInputTarget) {
      this.spotifySearchInputTarget.value = ''
    }
    if (this.hasSpotifySearchResultsTarget) {
      this.spotifySearchResultsTarget.classList.add('hidden')
      this.spotifySearchResultsTarget.innerHTML = ''
    }
    if (this.hasSpotifySelectedArtistTarget) {
      this.spotifySelectedArtistTarget.classList.add('hidden')
    }
    // Reset image/fallback visibility
    if (this.hasSpotifySelectedImageTarget) {
      this.spotifySelectedImageTarget.classList.add('hidden')
    }
    if (this.hasSpotifySelectedFallbackIconTarget) {
      this.spotifySelectedFallbackIconTarget.classList.remove('hidden')
    }
    if (this.hasSpotifyUriInputTarget) {
      this.spotifyUriInputTarget.value = ''
    }
  }

  updateSpotifyHiddenField(value) {
    if (!this.hasSpotifyUriHiddenTarget) return

    // Convert ID-only format to full URI
    if (/^[a-zA-Z0-9]+$/.test(value) && !value.startsWith('spotify:')) {
      value = `spotify:artist:${value}`
    }
    this.spotifyUriHiddenTarget.value = value
  }

  formatNumber(num) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // "spotify:artist:4tZ..." -> "4tZ...". Also accepts a bare id or a URL.
  spotifyIdFrom(value) {
    const text = (value || "").toString().trim()
    if (!text) return ""

    return text.split(/[?#]/)[0].split(/[/:]/).filter(Boolean).pop() || ""
  }

  // Matches the Apple widget, which leads with "Apple ID: 123".
  spotifyInfoText(spotifyUri, followersDisplay, genres) {
    const id = this.spotifyIdFrom(spotifyUri)
    const parts = []

    if (id) parts.push(`Spotify ID: ${id}`)
    parts.push(`${followersDisplay || "0"} followers`)

    const genresDisplay = (genres || []).slice(0, 2).join(", ")
    if (genresDisplay) parts.push(genresDisplay)

    return parts.join(" - ")
  }
}
